"use client"

import { useRef } from "react"

import { useTranslations } from "next-intl"

import { HeaderSearch } from "@/components/marketplace/layout/header-search"
import { CloseIcon, IconButton, PageVeil } from "@/components/ui"
import { useFocusTrap } from "@/lib/hooks/use-focus-trap"
import { usePinnedPanel } from "@/lib/hooks/use-pinned-panel"
import { useScrollLock } from "@/lib/hooks/use-scroll-lock"

const CLOSE_DISC = "size-fab shadow-raised press-glyph bg-surface-inverse text-fg-on-inverse hover:bg-surface-inverse/88 shrink-0 rounded-full"
const SUGGESTIONS_CARD = "z-sticky motion-safe:animate-fade-in-top fixed inset-x-375 touch-pan-y overflow-y-auto overscroll-contain"

interface MobileSearchSheetProps {
  value: string
  onValueChange: (value: string) => void
  onSubmitTerm: (term: string) => void
  onClose: () => void
}

export const MobileSearchSheet = ({ value, onValueChange, onSubmitTerm, onClose }: MobileSearchSheetProps) => {
  const t = useTranslations("nav")
  const panelRef = useRef<HTMLDivElement>(null)
  const pinnedCard = usePinnedPanel()
  useScrollLock(true)
  useFocusTrap(panelRef, true)

  return (
    <>
      <PageVeil onDismiss={onClose} label={t("closeSearch")} className="touch-none lg:hidden" />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label={t("openSearch")} className="pointer-events-auto flex items-center gap-250">
        <IconButton aria-label={t("closeSearch")} variant="ghost" icon={<CloseIcon size={24} />} onClick={onClose} className={CLOSE_DISC} />
        <HeaderSearch
          autoFocusOnMount
          tone="surface"
          size="nav"
          submit
          submitVariant="glyph"
          glyph={false}
          placeholder={t("searchPlaceholderShort")}
          className="min-w-0 flex-1"
          panelClassName={SUGGESTIONS_CARD}
          panelStyle={pinnedCard}
          value={value}
          onValueChange={onValueChange}
          onSubmitTerm={onSubmitTerm}
          onDismiss={onClose}
        />
      </div>
    </>
  )
}
